<template>
  <FeaturePageLayout
    :pageTitle="meta.title"
    :pageDescription="meta.description"
  >
    <PageHeaderFeature
        wide
        title="Capture more leads with campaign-ready landing pages"
        subtitleText="Build, publish and track landing pages yourself, with no web designer or coding skills needed."
        buttonText="Get started"
        buttonLink="Pricing"
      ></PageHeaderFeature>

      <SectionHero
      theme="orange"
      rounded
      image="landing-pages-hero"
      title="Turn more clicks into customers"
      :accordion="[
        {
          name: 'A unique page for every campaign',
          content:
            'Create a unique landing page for every campaign, audience or offer, so you stop sending every visitor to the same page.',
        },
        {
          name: 'A consistent message',
          content:
            'Keep your message consistent throughout the user journey, from the email or ad they clicked to the page they land on.',
        },
        {
          name: 'Looks great everywhere',
          content:
            'Build fast, professional pages that work everywhere, from mobile to desktop, with nothing extra to configure.',
        },
        {
          name: 'More leads, same spend',
          content:
            'Increase your conversion rate and generate more leads without spending more on ads.',
        },
      ]"
      selectedItem="A unique page for every campaign"
    ></SectionHero>

    <SectionTwoColumnRight
      title="Launch campaigns without the wait"
      content="Easily build landing pages, with a user-friendly drag and drop tool and ready-made templates, so you can spend less time waiting for designer support, and more time bringing your ideas to life. Build, preview and publish from one place, update and republish whenever you need, and cut design and agency costs."
      image="landing-pages-builder"
    ></SectionTwoColumnRight>

    <SectionTwoColumnLeft
      title="See what's working, without the guesswork"
      content="Make smarter marketing decisions with a data-driven view of what happens after every click. See which campaigns drive engagement and conversions with built-in landing page analytics and automatic site tracking. See landing page performance alongside your email results, understand visitor behavior after they click, and identify your best-performing campaigns so you can repeat what works."
      image="landing-pages-analytics"
    ></SectionTwoColumnLeft>

    <SectionTwoColumnRight
      title="Capture leads while interest is high"
      content="Improve lead capture and follow up at the right time. Turn visitors into leads and keep them moving through the journey with embedded Forms connected to your existing Automations. New leads go straight into your workflows, timely follow-ups trigger automatically, and you can focus your time and budget on what delivers results."
      image="landing-pages-forms"
    ></SectionTwoColumnRight>

    <section class="content-section content-section--grey pt-30 pb-30 mt-40">
      <div class="container-fluid">
        <div class="row between-xs">
          <div class="col-xs-12 col-sm-5">
            <h4 class="mb-6 sm:mb-0">
              Everything you need to build better landing pages
            </h4>
          </div>
          <div class="col-xs-12 col-sm-6">
            <p class="p--lg">
              Familiar tools, built-in forms and analytics, and connections to
              the rest of Transpond, so you can publish campaign-ready pages
              without leaving the product.
            </p>
          </div>
        </div>

        <ScrollReveal stagger class="row mt-10 sm:mt-16">
          <div class="col-xs-12 col-sm-6 col-md-4 mb-10">
            <h5 class="mb-4">Familiar editor</h5>
            <p class="p--lg">
              Landing Pages uses the same engine as the Transpond email editor,
              so if you've built an email, you already know how to build a page.
            </p>
          </div>
          <div class="col-xs-12 col-sm-6 col-md-4 mb-10">
            <h5 class="mb-4">Looks good on all devices</h5>
            <p class="p--lg">
              Pages you create adapt to desktop, tablet and mobile with nothing
              to configure.
            </p>
          </div>
          <div class="col-xs-12 col-sm-6 col-md-4 mb-10">
            <h5 class="mb-4">Forms built in</h5>
            <p class="p--lg">
              Drop a Form onto any page, with no separate form builder needed.
            </p>
          </div>
          <div class="col-xs-12 col-sm-6 col-md-4 mb-10 md:mb-0">
            <h5 class="mb-4">Built-in analytics</h5>
            <p class="p--lg">
              See visitor activity and page performance, reported right inside
              Transpond.
            </p>
          </div>
          <div class="col-xs-12 col-sm-6 col-md-4 mb-10 md:mb-0">
            <h5 class="mb-4">Edit and republish anytime</h5>
            <p class="p--lg">
              Update a live page whenever you need and your changes go out
              instantly.
            </p>
          </div>
          <div class="col-xs-12 col-sm-6 col-md-4 mb-10 md:mb-0">
            <h5 class="mb-4">Connect to your other campaigns</h5>
            <p class="p--lg">
              Works with your emails, Forms and Automations. You can even add
              the Conversations chatbot to any page in a couple of clicks.
            </p>
          </div>
        </ScrollReveal>
      </div>
    </section>

    <SectionStepsTwoColumnRight
      title="Create, launch, and track in three simple steps"
      image="landing-pages-get-started"
      :steps="[
        {
          title: 'Build your page',
          content: 'Pick a template and drop in your content.',
        },
        {
          title: 'Share your campaign',
          content: 'Link the page from emails, ads or social posts.',
        },
        {
          title: 'Track your results',
          content: 'See who visited, engaged and converted.',
        },
      ]"
    ></SectionStepsTwoColumnRight>

    <section class="content-section mt-20 sm:mt-40 mb-20" data-gtm-context="FAQ">
      <div class="container-fluid">
        <div class="row between-xs">
          <div class="col-xs-12 col-sm-5">
            <h4 class="mb-6 sm:mb-0">FAQs</h4>
          </div>
          <div class="col-xs-12 col-sm-6">
            <div class="accordion">
              <AccordionItem
                v-for="item in faqs"
                :key="item.name"
                :active="item.name === selectedFaq"
                @setSelected="() => setSelectedFaq(item)"
              >
                {{ item.name }}
                <template v-slot:content>
                  {{ item.content }}
                </template>
              </AccordionItem>
            </div>
          </div>
        </div>
      </div>
    </section>
  </FeaturePageLayout>
</template>

<script>
import FeaturePageLayout from "@/layouts/FeaturePageLayout.vue";
import PageHeaderFeature from "@/components/Header/PageHeaderFeature.vue";
import SectionHero from "@/components/Features/SectionHero.vue";
import SectionTwoColumnRight from "@/components/ContentSections/SectionTwoColumnRight";
import SectionTwoColumnLeft from "@/components/ContentSections/SectionTwoColumnLeft";
import SectionStepsTwoColumnRight from "@/components/ContentSections/SectionStepsTwoColumnRight.vue";
import AccordionItem from "@/components/Accordion/AccordionItem.vue";
import ScrollReveal from "@/components/ScrollReveal.vue";

export default {
  name: "LandingPages",
  components: {
    FeaturePageLayout,
    PageHeaderFeature,
    SectionHero,
    SectionTwoColumnRight,
    SectionTwoColumnLeft,
    SectionStepsTwoColumnRight,
    AccordionItem,
    ScrollReveal,
  },
  data() {
    return {
      meta: {
        title: "Landing Pages - Transpond",
        description:
          "Capture more leads with campaign-ready landing pages built in minutes. Build, publish and track landing pages yourself, with no web designer or coding skills needed.",
      },
      selectedFaq: "Do I need a web designer to create landing pages?",
      faqs: [
        {
          name: "Do I need a web designer to create landing pages?",
          content:
            "No. Landing Pages is built so anyone can easily create and publish a professional page themselves. You can choose from one of our pre-built templates or start from scratch and save blocks to use again.",
        },
        {
          name: "Can I create multiple landing pages?",
          content:
            "Yes. Create as many pages as you need for different campaigns, audiences or offers. Note, different plans have different limits for Landing Pages.",
        },
        {
          name: "Will my landing pages work on mobile?",
          content:
            "Yes. Every page adapts to any screen size automatically, with nothing to set up.",
        },
        {
          name: "Can I collect leads through my landing pages?",
          content:
            "Yes. Form submissions go straight into your contact lists and can trigger your existing Automations.",
        },
        {
          name: "Can I track landing page performance?",
          content:
            "Yes. Analytics are built in, so you can see how each page performs without any separate reporting tools.",
        },
      ],
    };
  },
  methods: {
    setSelectedFaq(item) {
      this.selectedFaq = item.name;
    },
  },
};
</script>
